import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Menu,
  MenuItem,
  Button,
  Typography,
  Fade,
  Grid,
  Divider,
} from "@mui/material";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import configLocal from "../../config/config.local.json";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

/* ─── Enterprise Brand Palette ── */
const brand = {
  carbon: "#1C1C1C", // Deep neutral for high-end text
  slate: "#475569", // Professional secondary text
  enterpriseBlue: "#0F172A", // Midnight blue for active states
  accent: "#2563EB", // Trusted blue for links/actions
  border: "#E2E8F0", // Crisp, subtle borders
  surface: "#F8FAFC", // Very light grey for hover states
  white: "#FFFFFF",
};

const ShopPagesBar = () => {
  const BASE_URL = configLocal.baseURL;
  const navigate = useNavigate();
  const location = useLocation();
  const shopParam = "test.spadeplatform.com";

  const [categories, setCategories] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/public/shop/categories/all?shop=${shopParam}`
        );
        setCategories(res.data?.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCategories();
  }, [BASE_URL]);

  const handleOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setActiveCategory(category);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveCategory(null);
  };

  return (
    <Box
      component="nav"
      sx={{
        backgroundColor: brand.white,
        borderBottom: `1px solid ${brand.border}`,
        position: "sticky",
        top: { xs: "56px", sm: "64px", md: "120px" },
        zIndex: 1000,
        // Enterprise look uses very subtle shadows
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: { xs: "48px", md: "52px" },
            gap: 1,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {categories.map((cat) => {
            const isActive = location.pathname.includes(
              `/shop/category/${cat.id}`
            );
            const hasSubs = cat.subCategories?.length > 0;

            return (
              <Box
                key={cat.id}
                onMouseLeave={handleClose}
                sx={{ height: "100%" }}
              >
                <Button
                  onMouseEnter={(e) => handleOpen(e, cat)}
                  onClick={() => navigate(`/shop/category/${cat.id}`)}
                  endIcon={
                    hasSubs && (
                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: "14px !important",
                          transition: "transform 0.2s",
                          transform:
                            activeCategory?.id === cat.id
                              ? "rotate(180deg)"
                              : "none",
                        }}
                      />
                    )
                  }
                  sx={{
                    color: isActive ? brand.enterpriseBlue : brand.slate,
                    fontWeight: isActive ? 700 : 500,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    px: 2,
                    height: "100%",
                    borderRadius: 0,
                    minWidth: "auto",
                    transition: "all 0.2s",
                    "&:hover": {
                      color: brand.accent,
                      backgroundColor: brand.surface,
                    },
                    // Indicator line
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -1,
                      left: 0,
                      width: "100%",
                      height: "2.5px",
                      backgroundColor: brand.accent,
                      transform: isActive ? "scaleX(1)" : "scaleX(0)",
                      transition: "transform 0.2s ease",
                    },
                  }}
                >
                  {cat.name}
                </Button>

                <Menu
                  open={Boolean(anchorEl) && activeCategory?.id === cat.id}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  TransitionComponent={Fade}
                  disableScrollLock
                  elevation={0}
                  MenuListProps={{
                    onMouseEnter: () => setAnchorEl(anchorEl),
                    onMouseLeave: handleClose,
                  }}
                  sx={{ pointerEvents: "none" }} // Allows mouse to pass through to Paper
                  PaperProps={{
                    sx: {
                      pointerEvents: "auto",
                      mt: "1px",
                      minWidth: { md: 280 },
                      maxWidth: 400,
                      borderRadius: "0 0 8px 8px",
                      border: `1px solid ${brand.border}`,
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                      p: 1,
                    },
                  }}
                >
                  <Box sx={{ p: 1.5 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: brand.slate,
                        fontWeight: 700,
                        letterSpacing: 1,
                      }}
                    >
                      Explore {cat.name}
                    </Typography>
                    <Divider sx={{ my: 1, mb: 2 }} />
                    <Grid container spacing={1}>
                      {(cat.subCategories || []).map((sub) => (
                        <Grid item xs={12} key={sub.id}>
                          <MenuItem
                            onClick={() => {
                              navigate(`/shop/category/${sub.id}`);
                              handleClose();
                            }}
                            sx={{
                              borderRadius: "6px",
                              fontSize: "0.85rem",
                              py: 1,
                              color: brand.carbon,
                              "&:hover": {
                                color: brand.accent,
                                backgroundColor: brand.surface,
                              },
                            }}
                          >
                            {sub.name}
                          </MenuItem>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Menu>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default ShopPagesBar;
